import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import { getCurrentMarket } from '@utils/general'

export const useTankSupply = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()
  const useTankSupplyOverviewQuery = (filter) =>
    useQuery({
      queryKey: ['tankSupplyOverview', filter],
      queryFn: ({ queryKey }) => api.post('store/tank/supply/find', { ...filter }),
      refetchOnWindowFocus: false,
    })

  const useTankSupplyOptionsMetadataQuery = () =>
    useQuery({
      queryKey: ['tankSupplyOptionsMetadata'],
      queryFn: () => api.post('store/tank/supply/options_metadata'),
      refetchOnWindowFocus: false,
    })

  const useTankSupplyOptionsQuery = (payload) =>
    useQuery({
      queryKey: ['tankSupplyOverview', payload],
      queryFn: ({ queryKey }) => api.post('store/tank/supply/options', payload),
      refetchOnWindowFocus: false,
      enabled: !!payload,
    })

  const removeDefaultMutation = () =>
    useMutation({
      mutationFn: (storeTanks) =>
        api.post('store/tank/supply/assign', {
          store_tanks: storeTanks,
          curve_ids: [],
          effective_from: Date.now(),
          effective_to: Date.now(),
        }),

      // When mutate is called:
      onMutate: async (storeTanks) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['tankSupplyOverview'],
        })

        // Snapshot the previous value
        const previousQueries = queryClient.getQueriesData({
          queryKey: ['tankSupplyOverview'],
        })

        // Optimistically update to the new value
        queryClient.setQueriesData({ queryKey: ['tankSupplyOverview'] }, (oldData) => {
          if (!oldData) return
          return {
            ...oldData,
            rows: oldData?.rows?.map((tank) => {
              if (storeTanks?.map((storeTank) => storeTank.id).includes(tank.id)) {
                return { ...tank, tank_supply_default: undefined }
              }
              return tank
            }),
          }
        })

        // Return a context with the previous and new todo
        return { previousQueries, storeTanks }
      },
      onError: (err, newSite, context) => {
        queryClient.setQueriesData({ queryKey: ['tankSupplyOverview'] }, context.previousQueries)
      },
    })
  const removeOverrideMutation = () =>
    useMutation({
      mutationFn: (storeTanks) =>
        api.post('store/tank/supply/assign_override', {
          store_tanks: storeTanks,
          curve_ids: [],
          effective_from: Date.now(),
          effective_to: Date.now(),
        }),

      // When mutate is called:
      onMutate: async (storeTanks) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['tankSupplyOverview'],
        })

        // Snapshot the previous value
        const previousQueries = queryClient.getQueriesData({
          queryKey: ['tankSupplyOverview'],
        })

        // Optimistically update to the new value
        queryClient.setQueriesData({ queryKey: ['tankSupplyOverview'] }, (oldData) => {
          if (!oldData) return
          return {
            ...oldData,
            rows: oldData?.rows?.map((tank) => {
              if (storeTanks?.map((storeTank) => storeTank.id).includes(tank.id)) {
                return { ...tank, tank_supply_override: undefined }
              }
              return tank
            }),
          }
        })

        // Return a context with the previous and new todo
        return { previousQueries, storeTanks }
      },
      onError: (err, newSite, context) => {
        queryClient.setQueriesData({ queryKey: ['tankSupplyOverview'] }, context.previousQueries)
      },
    })

  const useTankSupplyMetadataQuery = () =>
    useQuery({
      queryKey: ['tankSupplyMetadata'],
      queryFn: () => api.post('store/tank/supply/metadata', { market: getCurrentMarket() }),
    })

  return {
    useTankSupplyOverviewQuery,
    useTankSupplyOptionsMetadataQuery,
    useTankSupplyOptionsQuery,
    removeOverrideMutation,
    removeDefaultMutation,
    useTankSupplyMetadataQuery,
  }
}
